"use client";

import type { ChangeEvent } from "react";
import { useDeferredValue, useState } from "react";
import { Notice, TextAreaField } from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, HINT, INPUT } from "@/components/tool-kit/styles";
import { LatexWordCounterResults } from "@/components/tools/latex-dev/latex-word-counter-results";
import { useToolUi } from "@/components/tools/tool-ui";
import { texCount } from "@/lib/tool-kit/latex-dev/texcount";

/** English strings. A translated content file overrides these through `ui`. */
export const LATEX_WORD_COUNTER_STRINGS = {
  sourceLabel: "Your LaTeX source",
  sourcePlaceholder:
    "Paste the .tex source here — the whole file, from \\documentclass to \\end{document}, or just a section.",
  sourceHint:
    "Counted in your browser by a port of TeXcount's rules. Nothing is uploaded.",
  uploadLabel: "Or open a .tex file from your computer",
  loadExample: "Load the example",
  clear: "Clear",
  example:
    "\\documentclass{article}\n\\usepackage{hyperref}\n\\title{Reading Habits of Students}\n\\begin{document}\n\\maketitle\n\\begin{abstract}\nWe surveyed forty students about how they read.\n\\end{abstract}\n\\section{Introduction}\nReading is a well-known predictor of success, e.g. in college.\\footnote{See Smith (2020).} We ask a simple question: how much do students read?\n\n\\section{Method}\nParticipants answered $n = 40$ questions \\cite{smith2020}.\n\\begin{equation}\n  \\bar{x} = \\frac{1}{n}\\sum_i x_i\n\\end{equation}\n\\begin{table}[h]\n  \\caption{Hours read per week}\n  \\begin{tabular}{lr}\n    Group & Hours \\\\\n    A & 4 \\\\\n  \\end{tabular}\n\\end{table}\n\\end{document}",
  emptyPrompt: "Paste LaTeX source, or load the example, to count its words.",
  results: "Word count",
  wordsText: "Words in text",
  wordsHeaders: "Words in headers",
  wordsOther: "Words in captions and footnotes",
  wordsTotal: "Text + headers + captions",
  sumLabel: "TeXcount -sum (adds math)",
  sumHint:
    'The first three cards are the three counts TeXcount reports. The "-sum" figure also adds one for every formula, as TeXcount\'s -sum option does.',
  headers: "Headings",
  floats: "Figures and tables",
  inlineMath: "Inline formulas",
  displayMath: "Displayed formulas",
  copySummary: "Copy counts",
  copied: "Copied!",
  summaryTemplate:
    "Words in text: {text}. Words in headers: {headers}. Words in captions and footnotes: {other}. Total: {total}.",
  errorsHeading:
    "one: {count} problem in the source.|other: {count} problems in the source.",
  errorLine: "line {line}: {message}",
  includesNote:
    "one: {count} file is pulled in with \\input or \\include ({files}) and is not counted here. Paste it as well, or count it separately.|other: {count} files are pulled in with \\input or \\include ({files}) and are not counted here. Paste them as well, or count them separately.",
  sectionsHeading: "Counts by section",
  colSection: "Section",
  colText: "Text",
  colHeaders: "Headers",
  colOther: "Captions, notes",
  beforeFirst: "Before the first section",
  frequentHeading: "Most frequent words",
  colWord: "Word",
  colCount: "Times",
  previewHeading: "What was counted",
  previewLabel: "Your LaTeX with the counted words highlighted",
  previewCut: "Showing the first {count} characters.",
  legendText: "text",
  legendHeader: "headers",
  legendOther: "captions and footnotes",
} as const;

export default function LatexWordCounterWidget() {
  const strings = useToolUi(LATEX_WORD_COUNTER_STRINGS);
  const [source, setSource] = useState("");
  const deferred = useDeferredValue(source);
  const result = deferred.trim() === "" ? null : texCount(deferred);

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) {
      setSource(await file.text());
    }
    event.target.value = "";
  }

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.sourceHint}
        id="ltxwc-source"
        label={strings.sourceLabel}
        onChange={setSource}
        placeholder={strings.sourcePlaceholder}
        rows={12}
        value={source}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource(strings.example)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource("")}
          type="button"
        >
          {strings.clear}
        </button>
        <label className={HINT} htmlFor="ltxwc-file">
          {strings.uploadLabel}
        </label>
        <input
          accept=".tex,.latex,.txt,text/plain,application/x-tex"
          className={`${INPUT} w-auto`}
          id="ltxwc-file"
          onChange={onFile}
          type="file"
        />
      </div>
      <div aria-live="polite">
        {result === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <LatexWordCounterResults
            result={result}
            source={deferred}
            strings={strings}
          />
        )}
      </div>
    </section>
  );
}
